import React, { useEffect, useState, useRef } from 'react';
import VisibilityOutlined from '@mui/icons-material/VisibilityOutlined';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import Reply from '@mui/icons-material/Reply';
import axios from '../axiosConfig';
import ProductInfoContent from '../components/ProductInfoContent';
import { completeImageUrl } from '../utils/imageUrl';
import { useNavigate } from 'react-router-dom';
import HeartIcon from '../components/HeartIcon';
import '../components/ProductsList.css';
import '../components/AuthPopup.css';
import './FavouritePage.css';
import './CartPage.css';
import { getProductImageUrl } from '../utils/productImage';
import { formatSizeSpaced } from '../utils/formatSize';
import FeedCaptionMeta from '../components/FeedCaptionMeta';


export default function FavouritePage() {
  const navigate = useNavigate();
  const [allProducts, setAllProducts] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listMode, setListMode] = useState(false);
  const [displayProducts, setDisplayProducts] = useState([]);
  const [clickedProductId, setClickedProductId] = useState(null);
  // index of the product currently on screen in list mode (drives the shop header)
  const [activeIndex, setActiveIndex] = useState(0);
  const [likedIds, setLikedIds] = useState([]);
  const [cartIds, setCartIds] = useState([]);
  const [currentUserAccountId, setCurrentUserAccountId] = useState(null);
  const [clickTimeouts, setClickTimeouts] = useState({});
  const [flippedProductId, setFlippedProductId] = useState(null);
  // price filter UI was removed from the page; keep the (empty) value the list logic still reads
  const [appliedPriceFilter] = useState({ min: '', max: '' });
  const [showBuyDialog, setShowBuyDialog] = useState(false);
  const [buyDialogProduct, setBuyDialogProduct] = useState(null);
  const [buyDialogQuantity, setBuyDialogQuantity] = useState(1);
  const [toasts, setToasts] = useState([]);
  const clickedProductRef = useRef(null);
  const allProductsRef = useRef([]);
  const displayProductsRef = useRef([]);
  
  // Keep refs in sync with state
  useEffect(() => {
    allProductsRef.current = allProducts;
  }, [allProducts]);
  
  useEffect(() => {
    displayProductsRef.current = displayProducts;
  }, [displayProducts]);

  // Fetch liked products once on mount
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        // the API pages its list : take every page
        const pageSize = 20;
        let fetchedProducts = [];
        for (let page = 1; ; page += 1) {
          const response = await axios.get(`/api/v1/products?liked=true&page=${page}&per_page=${pageSize}`);
          const batch = response.data || [];
          fetchedProducts = fetchedProducts.concat(batch);
          if (batch.length < pageSize) break;
        }
        setAllProducts(fetchedProducts);
        setProducts(fetchedProducts);
        
        if (fetchedProducts.length > 0) {
          // Initialize liked and cart state
          if (localStorage.getItem('authToken')) {
            // Fetch current user account_id
            try {
              const userResponse = await axios.get('/api/v1/users/current');
              if (userResponse.data.account_id) {
                setCurrentUserAccountId(userResponse.data.account_id);
              }
            } catch (error) {
              console.error('Error fetching user account_id:', error);
            }
            const initialLiked = fetchedProducts
              .filter((p) => p.liked_by_current_user)
              .map((p) => p.id);
            setLikedIds(initialLiked);
            const initialCart = fetchedProducts
              .filter((p) => p.in_cart)
              .map((p) => p.id);
            setCartIds(initialCart);
          }
        }
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  // Filter products client-side when filters are applied
  useEffect(() => {
    let filtered = [...allProducts];
    
    if (appliedPriceFilter.min || appliedPriceFilter.max) {
      filtered = filtered.filter(product => {
        // Get product price from variations
        let productPrice = null;
        if (product.product_variations && product.product_variations.length > 0) {
          // Use the lowest price from variations
          const prices = product.product_variations.map(v => parseFloat(v.price) || 0);
          productPrice = Math.min(...prices);
        }
        
        if (productPrice === null) return false;
        
        // Check min price
        if (appliedPriceFilter.min && productPrice < parseFloat(appliedPriceFilter.min)) {
          return false;
        }
        
        // Check max price
        if (appliedPriceFilter.max && productPrice > parseFloat(appliedPriceFilter.max)) {
          return false;
        }
        
        return true;
      });
    }
    
    setProducts(filtered);
  }, [appliedPriceFilter, allProducts]);
  
  const openInfo = (product, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setFlippedProductId(product.id);
  };

  const closeInfo = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setFlippedProductId(null);
  };

  const handleShare = async (productId, e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    
    const productUrl = `${window.location.origin}/product/${productId}`;
    
    try {
      // Try to use the Clipboard API
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(productUrl);
        addToast('Посилання скопійовано!');
      } else {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = productUrl;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        addToast('Посилання скопійовано!');
      }
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      addToast('Помилка копіювання');
    }
  };

  const toggleLike = (id, e) => {
    e.stopPropagation();
    e.preventDefault();
    const token = localStorage.getItem('authToken');
    if (!token) {
      window.dispatchEvent(
        new CustomEvent('openAuthPopup', { detail: { redirectRoute: window.location.pathname } })
      );
      return;
    }
    axios
      .post(`/api/v1/products/${id}/toggle_like`)
      .then((response) => {
        if (!response.data.liked) {
          // If unliked, remove from products and allProducts (for grid mode)
          // But keep in displayProducts (for list mode) so it stays visible
          setProducts(prev => prev.filter(p => p.id !== id));
          setAllProducts(prev => prev.filter(p => p.id !== id));
          // Don't remove from displayProducts - keep it visible in list mode
        } else {
          // If liked again, restore product to grid
          // Use refs to check synchronously and avoid duplicates
          const existingInAll = allProductsRef.current.find(p => p.id === id);
          const existingInDisplay = displayProductsRef.current.find(p => p.id === id);
          
          if (existingInAll) {
            // Already in allProducts, just ensure it's in products if filters allow
            setProducts(prevProducts => {
              if (prevProducts.find(p => p.id === id)) {
                return prevProducts; // Already in products
              }
              // Apply filters
              let shouldInclude = true;
              if (appliedPriceFilter.min || appliedPriceFilter.max) {
                let productPrice = null;
                if (existingInAll.product_variations && existingInAll.product_variations.length > 0) {
                  const prices = existingInAll.product_variations.map(v => parseFloat(v.price) || 0);
                  productPrice = Math.min(...prices);
                }
                if (productPrice !== null) {
                  if (appliedPriceFilter.min && productPrice < parseFloat(appliedPriceFilter.min)) {
                    shouldInclude = false;
                  }
                  if (appliedPriceFilter.max && productPrice > parseFloat(appliedPriceFilter.max)) {
                    shouldInclude = false;
                  }
                } else {
                  shouldInclude = false;
                }
              }
              if (shouldInclude) {
                return [...prevProducts, existingInAll];
              }
              return prevProducts;
            });
          } else if (existingInDisplay) {
            // Found in displayProducts but not allProducts, add to both
            setAllProducts(prevAll => {
              if (prevAll.find(p => p.id === id)) {
                return prevAll; // Already exists (race condition check)
              }
              return [...prevAll, existingInDisplay];
            });
            setProducts(prevProducts => {
              if (prevProducts.find(p => p.id === id)) {
                return prevProducts; // Already exists (race condition check)
              }
              // Apply filters
              let shouldInclude = true;
              if (appliedPriceFilter.min || appliedPriceFilter.max) {
                let productPrice = null;
                if (existingInDisplay.product_variations && existingInDisplay.product_variations.length > 0) {
                  const prices = existingInDisplay.product_variations.map(v => parseFloat(v.price) || 0);
                  productPrice = Math.min(...prices);
                }
                if (productPrice !== null) {
                  if (appliedPriceFilter.min && productPrice < parseFloat(appliedPriceFilter.min)) {
                    shouldInclude = false;
                  }
                  if (appliedPriceFilter.max && productPrice > parseFloat(appliedPriceFilter.max)) {
                    shouldInclude = false;
                  }
                } else {
                  shouldInclude = false;
                }
              }
              if (shouldInclude) {
                return [...prevProducts, existingInDisplay];
              }
              return prevProducts;
            });
          } else {
            // Not found in either list, fetch from API
            axios.get(`/api/v1/products/${id}`)
              .then(productResponse => {
                const fetchedProduct = productResponse.data;
                setAllProducts(prevAll => {
                  // Check again in case it was added between async calls
                  if (prevAll.find(p => p.id === id)) {
                    return prevAll;
                  }
                  return [...prevAll, fetchedProduct];
                });
                setProducts(prevProducts => {
                  // Check again in case it was added between async calls
                  if (prevProducts.find(p => p.id === id)) {
                    return prevProducts;
                  }
                  // Apply filters
                  let shouldInclude = true;
                  if (appliedPriceFilter.min || appliedPriceFilter.max) {
                    let productPrice = null;
                    if (fetchedProduct.product_variations && fetchedProduct.product_variations.length > 0) {
                      const prices = fetchedProduct.product_variations.map(v => parseFloat(v.price) || 0);
                      productPrice = Math.min(...prices);
                    }
                    if (productPrice !== null) {
                      if (appliedPriceFilter.min && productPrice < parseFloat(appliedPriceFilter.min)) {
                        shouldInclude = false;
                      }
                      if (appliedPriceFilter.max && productPrice > parseFloat(appliedPriceFilter.max)) {
                        shouldInclude = false;
                      }
                    } else {
                      shouldInclude = false;
                    }
                  }
                  if (shouldInclude) {
                    return [...prevProducts, fetchedProduct];
                  }
                  return prevProducts;
                });
              })
              .catch(error => console.error('Error fetching product:', error));
          }
        }
        setLikedIds((prev) => {
          if (response.data.liked) {
            return [...prev, id];
          } else {
            return prev.filter((x) => x !== id);
          }
        });
      })
      .catch((error) => console.error('Error toggling like:', error));
  };

  const toggleCart = (product, e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    const token = localStorage.getItem('authToken');
    if (!token) {
      window.dispatchEvent(
        new CustomEvent('openAuthPopup', { detail: { redirectRoute: window.location.pathname } })
      );
      return;
    }
    // Show buy dialog instead of directly adding to cart
    setBuyDialogProduct(product);
    setBuyDialogQuantity(1);
    setShowBuyDialog(true);
  };

  const addToast = (message) => {
    const toastId = Date.now() + Math.random();
    const newToast = {
      id: toastId,
      message: message,
      hiding: false
    };
    
    setToasts(prev => [...prev, newToast]);
    
    // Hide toast after 2.5 seconds
    setTimeout(() => {
      setToasts(prev => prev.map(toast => 
        toast.id === toastId ? { ...toast, hiding: true } : toast
      ));
      
      // Remove toast after animation
      setTimeout(() => {
        setToasts(prev => prev.filter(toast => toast.id !== toastId));
      }, 300);
    }, 2500);
  };

  const handleBuyDialogConfirm = () => {
    if (!buyDialogProduct || !buyDialogProduct.product_variations || buyDialogProduct.product_variations.length === 0) {
      return;
    }
    
    // Check if user is trying to buy from themselves
    if (buyDialogProduct.account_id && currentUserAccountId && 
        String(buyDialogProduct.account_id) === String(currentUserAccountId)) {
      addToast('Покупка у себе не дозволена правилами');
      return;
    }
    
    const quantity = buyDialogQuantity || 0;
    if (quantity <= 0) {
      addToast('Введіть кількість');
      return;
    }
    
    const token = localStorage.getItem('authToken');
    if (!token) {
      window.dispatchEvent(
        new CustomEvent('openAuthPopup', { detail: { redirectRoute: window.location.pathname } })
      );
      setShowBuyDialog(false);
      return;
    }

    const variation = buyDialogProduct.product_variations.find(v => v.main) || buyDialogProduct.product_variations[0];
    if (!variation) {
      addToast('Помилка: варіанти товару недоступні');
      return;
    }
    
    // Quantity is number of portions (each portion is 10g)
    const itemsToAdd = quantity;
    
    // Add to cart with quantity - make multiple API calls like ProductsList
    const addToCartPromises = Array.from({ length: itemsToAdd }, () =>
      axios.post(`/api/v1/cart/add_to_cart`, { product_variation_id: variation.id })
    );
    
    Promise.all(addToCartPromises)
      .then(() => {
        setCartIds((prev) => {
          if (!prev.includes(buyDialogProduct.id)) {
            return [...prev, buyDialogProduct.id];
          }
          return prev;
        });
        setShowBuyDialog(false);
        addToast(`Додано ${quantity} порцій (${quantity * 10}g) в кошик!`);
        window.dispatchEvent(new CustomEvent('cartUpdated'));
      })
      .catch((error) => {
        console.error('Error adding to cart:', error);
        addToast('Помилка додавання в кошик');
        setShowBuyDialog(false);
      });
  };

  const handleMobileCardClick = (productId, e) => {
    // Detect mobile device
    const isMobile = window.innerWidth <= 768;
    
    if (!isMobile) {
      // Desktop behavior - allow navigation (but we don't navigate in profile list mode)
      return;
    }
    
    // Mobile behavior - prevent navigation and handle double-click for like
    e.preventDefault();
    e.stopPropagation();
    
    const now = Date.now();
    const lastClick = clickTimeouts[productId];
    
    if (lastClick && now - lastClick < 300) {
      // Double click detected - toggle like
      clearTimeout(clickTimeouts[`timeout_${productId}`]);
      toggleLike(productId, e);
      setClickTimeouts(prev => {
        const { [productId]: removed, [`timeout_${productId}`]: removedTimeout, ...rest } = prev;
        return rest;
      });
    } else {
      // Single click - do nothing, just update timestamp
      setClickTimeouts(prev => ({
        ...prev,
        [productId]: now
      }));
      
      // Set timeout to clear the click timestamp
      const timeoutId = setTimeout(() => {
        setClickTimeouts(prev => {
          const { [productId]: removed, [`timeout_${productId}`]: removedTimeout, ...rest } = prev;
          return rest;
        });
      }, 300);
      
      setClickTimeouts(prev => ({
        ...prev,
        [`timeout_${productId}`]: timeoutId
      }));
    }
  };


  const handleProductClick = (productId, productIndex) => {
    // Keep original order - don't reorder products
    setDisplayProducts(products);
    setClickedProductId(productId);
    setListMode(true);
  };

  // Listen for custom events from Header
  useEffect(() => {
    const checkListMode = () => {
      if (listMode) {
        window.dispatchEvent(new CustomEvent('profileListModeResponse'));
      }
    };
    
    const exitListMode = () => {
      if (listMode) {
        setListMode(false);
        setDisplayProducts([]);
        setClickedProductId(null);
        setFlippedProductId(null);
      }
    };
    
    window.addEventListener('checkProfileListMode', checkListMode);
    window.addEventListener('exitProfileListMode', exitListMode);
    
    return () => {
      window.removeEventListener('checkProfileListMode', checkListMode);
      window.removeEventListener('exitProfileListMode', exitListMode);
    };
  }, [listMode]);

  // start the shop header on the product that was opened
  useEffect(() => {
    if (!listMode || !clickedProductId) return;
    const index = displayProducts.findIndex((p) => p.id === clickedProductId);
    if (index >= 0) setActiveIndex(index);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listMode, clickedProductId]);

  // Notify Header when list mode changes
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('favouritesListModeChange', { detail: { listMode } }));
  }, [listMode]);



  // Scroll to clicked product when entering list mode
  useEffect(() => {
    if (listMode && displayProducts.length > 0 && clickedProductId) {
      const scrollToProduct = () => {
        const gridContainer = document.querySelector('.products__grid');
        if (!gridContainer) return;
        
        if (clickedProductRef.current) {
          const isMobile = window.innerWidth <= 768;
          if (isMobile) {
            // For mobile TikTok-style view, scroll the grid container to show clicked product
            const productTop = clickedProductRef.current.offsetTop;
            gridContainer.scrollTo({ top: productTop, behavior: 'instant' });
          } else {
            // Desktop: scroll to center the product
            clickedProductRef.current.scrollIntoView({ behavior: 'instant', block: 'center' });
          }
        }
      };
      
      // Scroll immediately and with delays to catch all rendering scenarios
      scrollToProduct();
      const timers = [
        setTimeout(scrollToProduct, 50),
        setTimeout(scrollToProduct, 150),
        setTimeout(scrollToProduct, 300),
        setTimeout(scrollToProduct, 500)
      ];
      
      return () => {
        timers.forEach(timer => clearTimeout(timer));
      };
    }
  }, [listMode, clickedProductId, displayProducts.length]);

  if (loading) {
    return (
      <div className="favourites-page">
        <div className="loading-indicator">
          <div className="loader"></div>
        </div>
      </div>
    );
  }

  // TikTok-style list mode - use exact same structure as ProductsList
  if (listMode && displayProducts.length > 0) {
    return (
      <div className="products__page favourites-page--list-mode">
        {(() => {
          const shopProduct = displayProducts[Math.min(activeIndex, displayProducts.length - 1)];
          if (!shopProduct) return null;
          const shopName = shopProduct.account_title || (shopProduct.account_name || '').replace(/^@/, '');
          return (
            <div className="profile-list-header">
              <div
                className="profile-list-header-info profile-list-header-info--link"
                onClick={() => navigate(shopProduct.account_name ? `/${shopProduct.account_name}` : '/@tearoom')}
                role="link"
                aria-label="Відкрити магазин"
              >
                {shopProduct.account_url ? (
                  <span className="profile-list-header-avatar">
                    <img
                    src={completeImageUrl(shopProduct.account_url)}
                    alt={shopName}
                    className="profile-list-header-avatar__img"
                  />
                  </span>
                ) : (
                  <span className="profile-list-header-image profile-list-header-image--placeholder" title={shopName}>
                    <span className="profile-list-header-image__name">{shopName}</span>
                  </span>
                )}
                <div className="profile-list-header-title-wrapper">
                  <span className="profile-list-header-title">{shopName}</span>
                </div>
              </div>
            </div>
          );
        })()}
        <div
          className="products__grid"
          onScroll={(e) => {
            const grid = e.currentTarget;
            if (grid.clientHeight > 0) setActiveIndex(Math.round(grid.scrollTop / grid.clientHeight));
          }}
        >
          {displayProducts.map((product, index) => {
            const imageUrl = getProductImageUrl(product);
            const isLiked = likedIds.includes(product.id);
            const isFlipped = flippedProductId === product.id;
            const isClickedProduct = product.id === clickedProductId;
            
            return (
              <div 
                key={product.id} 
                className="products__link"
                ref={isClickedProduct ? clickedProductRef : null}
                onClick={(e) => handleMobileCardClick(product.id, e)}
              >
                <div className={`products__item ${isFlipped ? 'products__item--flipped' : ''}`}>
                  <div
                    className="products__item-background"
                    style={{ backgroundImage: `url(${imageUrl})` }}
                  />
                  <div
                    className="products__item-foreground"
                    style={{ backgroundImage: `url(${imageUrl})` }}
                  />
                  {/* dark fade under the caption, same as the home feed */}
                  <div className="feed-caption-scrim" aria-hidden="true" />
                  {/* Profile button */}
                  <div className="products__corner products__corner-topright products__corner-topright-new">
                    <button
                      className="products__new-toggle"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        // same as the home feed: open the seller of this product
                        navigate(product.account_name ? `/${product.account_name}` : '/@tearoom');
                      }}
                      aria-label="Back to profile"
                      style={product.account_url ? {
                        backgroundImage: `url(${completeImageUrl(product.account_url)})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat'
                      } : {
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'rgba(255, 255, 255, 0.4)',
                        backdropFilter: 'blur(4px)'
                      }}
                    >
                      {!product.account_url && (
                        <span className="products__new-toggle-name">{product.account_title || (product.account_name || '').replace(/^@/, '') || 'Магазин'}</span>
                      )}
                    </button>
                  </div>
                  {/* Info button */}
                  <div className="products__corner products__corner-topright products__corner-topright-info">
                    <button
                      className="products__info-toggle"
                      onClick={(e) => isFlipped ? closeInfo(e) : openInfo(product, e)}
                      aria-label={isFlipped ? "Close info" : "Product info"}
                    >
                      {isFlipped ? '×' : <VisibilityOutlined className="products__info-icon-svg" />}
                    </button>
                  </div>
                  {/* Like button */}
                  <div className="products__corner products__corner-topright">
                    <button
                      className={`products__toggle ${isLiked ? 'liked' : 'unliked'}`}
                      onClick={(e) => toggleLike(product.id, e)}
                    >
                      <HeartIcon />
                    </button>
                  </div>
                  {/* Share button */}
                  <div className="products__corner products__corner-middle-right">
                    <button
                      className="products__share-toggle"
                      onClick={(e) => handleShare(product.id, e)}
                      aria-label="Share product"
                    >
                      <Reply className="products__share-icon" style={{ transform: 'scaleX(-1)' }} />
                    </button>
                  </div>
                  {/* Cart button */}
                  <div className="products__corner products__corner-bottomright">
                    <button
                      className={`products__cart-toggle ${cartIds.includes(product.id) ? 'inCart' : 'outCart'}`}
                      onClick={(e) => toggleCart(product, e)}
                    >
                      <ShoppingCartIcon className="products__cart-icon" />
                    </button>
                  </div>
                  {/* Product name and price caption - same markup as the home feed (ProductsList) */}
                  {product.product_variations && product.product_variations.length > 0 && (
                    <div className="feed-caption">
                      <span className="feed-caption__name">
                        {product.title}
                      </span>
                      <FeedCaptionMeta variation={product.product_variations.find(v => v.main) || product.product_variations[0]} />
                    </div>
                  )}
                  {/* Info card back */}
                  <div className="products__item-back">
                    <ProductInfoContent product={product} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        {showBuyDialog && buyDialogProduct && (
        <div className="auth-popup-overlay" onClick={() => setShowBuyDialog(false)}>
          <div className="auth-popup-content" onClick={(e) => e.stopPropagation()} style={{ padding: 0 }}>
            <div style={{ textAlign: 'center', paddingTop: '50px' }}>
              <div style={{ marginBottom: '24px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px', marginBottom: '20px' }}>
                  <button
                    onClick={() => {
                      const newQty = (buyDialogQuantity || 0) - 1;
                      setBuyDialogQuantity(Math.max(0, newQty));
                    }}
                    style={{
                      width: '36px',
                      height: '36px',
                      border: '2px solid #000',
                      borderRadius: '50%',
                      background: '#fff',
                      fontSize: '20px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      padding: 0
                    }}
                  >
                    −
                  </button>
                  <input
                    type="number"
                    min="0"
                    value={buyDialogQuantity || ''}
                    onChange={(e) => {
                      const val = e.target.value === '' ? '' : parseInt(e.target.value) || 0;
                      setBuyDialogQuantity(Math.max(0, val));
                    }}
                    style={{
                      width: '60px',
                      height: '40px',
                      border: '2px solid #000',
                      borderRadius: '8px',
                      textAlign: 'center',
                      fontSize: '20px',
                      fontWeight: 'bold',
                      padding: '0 8px'
                    }}
                  />
                  <button
                    onClick={() => {
                      const newQty = (buyDialogQuantity || 0) + 1;
                      setBuyDialogQuantity(newQty);
                    }}
                    style={{
                      width: '36px',
                      height: '36px',
                      border: '2px solid #000',
                      borderRadius: '50%',
                      background: '#fff',
                      fontSize: '20px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      padding: 0
                    }}
                  >
                    +
                  </button>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'center' }}>
                  <div style={{ 
                    fontSize: '18px', 
                    fontWeight: 'bold',
                    visibility: (buyDialogQuantity || 0) > 0 ? 'visible' : 'hidden'
                  }}>
                    {(() => {
                      const quantity = buyDialogQuantity || 0;
                      const grams = quantity * 10;
                      return grams > 0 ? `${grams}G` : '0';
                    })()}
                  </div>
                  <div style={{ 
                    fontSize: '20px', 
                    fontWeight: 'bold', 
                    color: 'var(--color-accent)',
                    visibility: (buyDialogQuantity || 0) > 0 ? 'visible' : 'hidden'
                  }}>
                    {(() => {
                      const variation = buyDialogProduct.product_variations.find(v => v.main) || buyDialogProduct.product_variations[0];
                      if (!variation) return '0';
                      const basePrice = parseFloat(variation.price) || 0;
                      const quantity = buyDialogQuantity || 0;
                      // Quantity is number of portions (each portion is 10g)
                      const totalPrice = quantity * basePrice;
                      return totalPrice % 1 === 0 ? totalPrice.toString() : totalPrice.toFixed(2);
                    })()} ₴
                  </div>
                </div>
                {buyDialogProduct && buyDialogProduct.account_id && currentUserAccountId && 
                 String(buyDialogProduct.account_id) === String(currentUserAccountId) && (
                  <div style={{
                    fontSize: '14px',
                    color: 'var(--color-accent)',
                    marginTop: '16px',
                    textAlign: 'center',
                    padding: '8px 16px',
                    backgroundColor: '#fff5f8',
                    borderRadius: '8px'
                  }}>
                    Покупка у себе не дозволена правилами
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '30px' }}>
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    handleBuyDialogConfirm();
                  }}
                  disabled={(buyDialogQuantity || 0) === 0 || (buyDialogProduct?.account_id && currentUserAccountId && String(buyDialogProduct.account_id) === String(currentUserAccountId))}
                  style={{
                    width: '56px',
                    height: '56px',
                    borderRadius: '50%',
                    background: ((buyDialogQuantity || 0) === 0 || (buyDialogProduct?.account_id && currentUserAccountId && String(buyDialogProduct.account_id) === String(currentUserAccountId))) ? '#ccc' : 'var(--color-accent)',
                    border: 'none',
                    cursor: ((buyDialogQuantity || 0) === 0 || (buyDialogProduct?.account_id && currentUserAccountId && String(buyDialogProduct.account_id) === String(currentUserAccountId))) ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: ((buyDialogQuantity || 0) === 0 || (buyDialogProduct?.account_id && currentUserAccountId && String(buyDialogProduct.account_id) === String(currentUserAccountId))) ? 'none' : '0 4px 12px rgba(31, 31, 31, 0.3)',
                    transition: 'all 0.3s ease',
                    marginRight: '20px',
                    marginBottom: '20px',
                    opacity: ((buyDialogQuantity || 0) === 0 || (buyDialogProduct?.account_id && currentUserAccountId && String(buyDialogProduct.account_id) === String(currentUserAccountId))) ? 0.5 : 1
                  }}
                  onMouseEnter={(e) => {
                    if ((buyDialogQuantity || 0) > 0 && !(buyDialogProduct?.account_id && currentUserAccountId && String(buyDialogProduct.account_id) === String(currentUserAccountId))) {
                      e.currentTarget.style.background = 'var(--color-accent-hover)';
                      e.currentTarget.style.transform = 'scale(1.1)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if ((buyDialogQuantity || 0) > 0 && !(buyDialogProduct?.account_id && currentUserAccountId && String(buyDialogProduct.account_id) === String(currentUserAccountId))) {
                      e.currentTarget.style.background = 'var(--color-accent)';
                      e.currentTarget.style.transform = 'scale(1)';
                    }
                  }}
                >
                  <ShoppingCartIcon style={{ color: '#fff', fontSize: '28px' }} />
                </button>
              </div>
            </div>
          </div>
        </div>
        )}
        {toasts.map((toast, index) => (
          <div 
            key={toast.id}
            className={`toast-notification ${toast.hiding ? 'toast-hiding' : ''}`}
            style={{ top: `${20 + index * 70}px` }}
          >
            <span>{toast.message}</span>
          </div>
        ))}
      </div>
    );
  }

  // Grid mode
  return (
    <div className="favourites-page">
      {products.length === 0 && !loading && (
        <div className="cart-list">
          <div className="cart-empty">
            <p>Немає обраних товарів</p>
          </div>
        </div>
      )}
      
      {products.length > 0 && (
        <div className="favourites-page-products-grid">
          {products.map((product, index) => {
            const imageUrl = getProductImageUrl(product);
            // plain price: the lowest one, with the size of that variation
            const variations = product.product_variations || [];
            const prices = variations.map((v) => parseFloat(v.price)).filter((n) => n > 0);
            const minPrice = prices.length > 0 ? Math.min(...prices) : null;
            const cheapest = variations.find((v) => parseFloat(v.price) === minPrice);
            const minSize = cheapest && cheapest.size ? formatSizeSpaced(cheapest.size) : null;
            return (
              <div
                key={product.id}
                className="favourites-page-product-item"
                onClick={() => handleProductClick(product.id, index)}
              >
                <div className="favourites-page-product-thumb">
                  <img className="favourites-page-product-image" src={imageUrl} alt="" loading="lazy" decoding="async" />
                  <div className="favourites-page-product-info">
                    <span className="favourites-page-product-name">{product.title}</span>
                    {minPrice !== null && (
                      <span className="favourites-page-product-price">
{minPrice % 1 === 0 ? minPrice : minPrice.toFixed(2)} ₴
{minSize && <span className="favourites-page-product-size">{minSize}</span>}
</span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {showBuyDialog && buyDialogProduct && (
        <div className="auth-popup-overlay" onClick={() => setShowBuyDialog(false)}>
          <div className="auth-popup-content" onClick={(e) => e.stopPropagation()} style={{ padding: 0 }}>
            <div style={{ textAlign: 'center', paddingTop: '50px' }}>
              <div style={{ marginBottom: '24px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px', marginBottom: '20px' }}>
                  <button
                    onClick={() => {
                      const newQty = (buyDialogQuantity || 0) - 1;
                      setBuyDialogQuantity(Math.max(0, newQty));
                    }}
                    style={{
                      width: '36px',
                      height: '36px',
                      border: '2px solid #000',
                      borderRadius: '50%',
                      background: '#fff',
                      fontSize: '20px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      padding: 0
                    }}
                  >
                    −
                  </button>
                  <input
                    type="number"
                    min="0"
                    value={buyDialogQuantity || ''}
                    onChange={(e) => {
                      const val = e.target.value === '' ? '' : parseInt(e.target.value) || 0;
                      setBuyDialogQuantity(Math.max(0, val));
                    }}
                    style={{
                      width: '60px',
                      height: '40px',
                      border: '2px solid #000',
                      borderRadius: '8px',
                      textAlign: 'center',
                      fontSize: '20px',
                      fontWeight: 'bold',
                      padding: '0 8px'
                    }}
                  />
                  <button
                    onClick={() => {
                      const newQty = (buyDialogQuantity || 0) + 1;
                      setBuyDialogQuantity(newQty);
                    }}
                    style={{
                      width: '36px',
                      height: '36px',
                      border: '2px solid #000',
                      borderRadius: '50%',
                      background: '#fff',
                      fontSize: '20px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      padding: 0
                    }}
                  >
                    +
                  </button>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'center' }}>
                  <div style={{ 
                    fontSize: '18px', 
                    fontWeight: 'bold',
                    visibility: (buyDialogQuantity || 0) > 0 ? 'visible' : 'hidden'
                  }}>
                    {(() => {
                      const quantity = buyDialogQuantity || 0;
                      const grams = quantity * 10;
                      return grams > 0 ? `${grams}G` : '0';
                    })()}
                  </div>
                  <div style={{ 
                    fontSize: '20px', 
                    fontWeight: 'bold', 
                    color: 'var(--color-accent)',
                    visibility: (buyDialogQuantity || 0) > 0 ? 'visible' : 'hidden'
                  }}>
                    {(() => {
                      const variation = buyDialogProduct.product_variations.find(v => v.main) || buyDialogProduct.product_variations[0];
                      if (!variation) return '0';
                      const basePrice = parseFloat(variation.price) || 0;
                      const quantity = buyDialogQuantity || 0;
                      // Quantity is number of portions (each portion is 10g)
                      const totalPrice = quantity * basePrice;
                      return totalPrice % 1 === 0 ? totalPrice.toString() : totalPrice.toFixed(2);
                    })()} ₴
                  </div>
                </div>
                {buyDialogProduct && buyDialogProduct.account_id && currentUserAccountId && 
                 String(buyDialogProduct.account_id) === String(currentUserAccountId) && (
                  <div style={{
                    fontSize: '14px',
                    color: 'var(--color-accent)',
                    marginTop: '16px',
                    textAlign: 'center',
                    padding: '8px 16px',
                    backgroundColor: '#fff5f8',
                    borderRadius: '8px'
                  }}>
                    Покупка у себе не дозволена правилами
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '30px' }}>
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    handleBuyDialogConfirm();
                  }}
                  disabled={(buyDialogQuantity || 0) === 0 || (buyDialogProduct?.account_id && currentUserAccountId && String(buyDialogProduct.account_id) === String(currentUserAccountId))}
                  style={{
                    width: '56px',
                    height: '56px',
                    borderRadius: '50%',
                    background: ((buyDialogQuantity || 0) === 0 || (buyDialogProduct?.account_id && currentUserAccountId && String(buyDialogProduct.account_id) === String(currentUserAccountId))) ? '#ccc' : 'var(--color-accent)',
                    border: 'none',
                    cursor: ((buyDialogQuantity || 0) === 0 || (buyDialogProduct?.account_id && currentUserAccountId && String(buyDialogProduct.account_id) === String(currentUserAccountId))) ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: ((buyDialogQuantity || 0) === 0 || (buyDialogProduct?.account_id && currentUserAccountId && String(buyDialogProduct.account_id) === String(currentUserAccountId))) ? 'none' : '0 4px 12px rgba(31, 31, 31, 0.3)',
                    transition: 'all 0.3s ease',
                    marginRight: '20px',
                    marginBottom: '20px',
                    opacity: ((buyDialogQuantity || 0) === 0 || (buyDialogProduct?.account_id && currentUserAccountId && String(buyDialogProduct.account_id) === String(currentUserAccountId))) ? 0.5 : 1
                  }}
                  onMouseEnter={(e) => {
                    if ((buyDialogQuantity || 0) > 0 && !(buyDialogProduct?.account_id && currentUserAccountId && String(buyDialogProduct.account_id) === String(currentUserAccountId))) {
                      e.currentTarget.style.background = 'var(--color-accent-hover)';
                      e.currentTarget.style.transform = 'scale(1.1)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if ((buyDialogQuantity || 0) > 0 && !(buyDialogProduct?.account_id && currentUserAccountId && String(buyDialogProduct.account_id) === String(currentUserAccountId))) {
                      e.currentTarget.style.background = 'var(--color-accent)';
                      e.currentTarget.style.transform = 'scale(1)';
                    }
                  }}
                >
                  <ShoppingCartIcon style={{ color: '#fff', fontSize: '28px' }} />
                </button>
              </div>
            </div>
          </div>
        </div>
        )}
        {toasts.map((toast, index) => (
        <div 
          key={toast.id}
          className={`toast-notification ${toast.hiding ? 'toast-hiding' : ''}`}
          style={{ top: `${20 + index * 70}px` }}
        >
          <span>{toast.message}</span>
        </div>
      ))}
    </div>
  );
}
